import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Laptop, Trash2 } from "lucide-react";
import { ApiErrorBanner } from "@/components/ui/ApiErrorBanner";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { formatDate } from "@/lib/formatters";
import type { CliDevice } from "../api/cli.api";
import { useCliDevices, useRevokeCliDevice } from "../hooks/useCli";

/**
 * Machines authorized through `aether login`. Kept apart from Access keys and
 * API keys: these are one-per-machine session credentials a person can name and
 * recognize, not credentials meant for pipelines.
 */
export function CliDevicesSection() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useCliDevices();
  const revoke = useRevokeCliDevice();
  const [revokeTarget, setRevokeTarget] = useState<CliDevice | null>(null);

  const devices = data?.devices ?? [];

  const confirmRevoke = () => {
    if (!revokeTarget) return;
    const credentialId = revokeTarget.credentialId;
    revoke.mutate(credentialId, {
      onSuccess: () => {
        setRevokeTarget((current) => (current?.credentialId === credentialId ? null : current));
      },
    });
  };

  return (
    <div className="rounded-xl border border-border bg-surface p-6">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-base font-semibold text-heading">
          <Laptop size={18} />
          {t("cli.devices.title")}
        </h3>
      </div>
      <p className="mt-1 text-xs text-muted">{t("cli.devices.subtitle")}</p>

      <ApiErrorBanner error={error ?? revoke.error} className="mt-4" />

      {isLoading ? <p className="mt-4 text-sm text-muted">{t("common.loading")}</p> : null}

      {!isLoading && !error && devices.length === 0 ? (
        <p className="mt-4 text-sm text-muted">{t("cli.devices.empty")}</p>
      ) : null}

      {devices.length > 0 ? (
        <ul className="mt-4 divide-y divide-border">
          {devices.map((device) => (
            <li key={device.credentialId} className="flex items-center justify-between gap-4 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-heading">
                  {device.deviceName || t("cli.devices.unnamed")}
                </p>
                <p className="mt-0.5 text-xs text-muted">
                  {[
                    device.clientVersion
                      ? `${device.clientName || "aether-cli"} ${device.clientVersion}`
                      : device.clientName,
                    device.clientPlatform,
                    `${t("cli.devices.added")}: ${formatDate(device.createdTime)}`,
                    `${t("cli.devices.expires")}: ${formatDate(device.expires)}`,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
              <button
                type="button"
                aria-label={t("cli.devices.revoke")}
                title={t("cli.devices.revoke")}
                onClick={() => {
                  if (!revoke.isPending) revoke.reset();
                  setRevokeTarget(device);
                }}
                className="shrink-0 rounded-lg border border-border bg-surface p-2 text-muted transition-colors hover:bg-danger/10 hover:text-danger"
              >
                <Trash2 size={16} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <ConfirmDialog
        open={revokeTarget !== null}
        title={t("cli.devices.revoke")}
        description={t("cli.devices.revokeConfirm", {
          name: revokeTarget?.deviceName || t("cli.devices.unnamed"),
        })}
        error={revoke.error ? (revoke.error.error ?? t("common.error")) : undefined}
        confirmLabel={t("cli.devices.revoke")}
        cancelLabel={t("common.cancel")}
        destructive
        isPending={revoke.isPending}
        closeOnConfirm={false}
        onConfirm={confirmRevoke}
        onClose={() => {
          if (revoke.isPending) return;
          revoke.reset();
          setRevokeTarget(null);
        }}
      />
    </div>
  );
}
